import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { AppUtils, SignolUtils } from "@src/utils";
import { useAuthContext, useDCContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";

import { ImageWithRetry, Loader, Tag } from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import { CUSTOMER_LOGO_URL } from "@src/constants/constants";

const ProfileCard = () => {
  const { profile } = useManagerContext();
  const auth = useAuthContext();
  const { getDynamicReplacement, existsDynamicReplacements } = useDCContext();

  const isMaritime = profile.customer.industry === "shipping";
  const customerCode = profile.customer.customerCode.toLowerCase();

  const {
    data: { numberOfVehicles } = {},
    isPending: vehiclesPending,
    error: vehiclesError,
  } = ManagerHooks.useGetVehicles(auth.activeRole);

  const logoUrl = getDynamicReplacement(
    `logos__${customerCode}`,
    {},
    CUSTOMER_LOGO_URL(customerCode),
  );
  const customerLogo = logoUrl ? (
    <ImageWithRetry className="h-8 w-auto" src={logoUrl} alt="logo" />
  ) : (
    ""
  );

  const typeOfContract = getDynamicReplacement(
    `customers__${customerCode}__contract_type`,
    {},
    "",
    true,
  );

  return (
    <CardContainer padding={6}>
      <div className="px-6 text-left text-sm space-y-2">
        <div className="py-6">{customerLogo}</div>

        {/* Contract Start / Customer Launch */}
        <div className="flex items-center">
          <span className="text-primary-700 font-bold pr-2">
            Launched with Signol:{" "}
          </span>
          {moment
            .utc(profile.customer.contractStartDate)
            .format("DD MMMM YYYY")}
        </div>

        {/* Contract Type */}
        {existsDynamicReplacements(
          `customers__${customerCode}__contract_type`,
        ) && (
          <div className="flex items-center">
            <span className="text-primary-700 font-bold pr-2">
              Contract type:
            </span>
            {getDynamicReplacement(`customers__${customerCode}__contract_type`)}
          </div>
        )}

        {/* Teams */}
        <div className="flex">
          <div className="text-primary-700 font-bold pt-2">Teams:</div>
          <div className="ps-2 flex flex-wrap gap-1">
            {(profile.teams || []).map((team, index) => (
              <Tag key={index} color="primary-100" textSize="sm">
                {team.alias || team.name}
              </Tag>
            ))}
          </div>
        </div>

        {typeOfContract && typeOfContract.toLowerCase() == "trial" && (
          <>
            {/* Vehicles */}
            <div className="flex items-center">
              <span className="text-primary-700 font-bold pr-2">
                Number of {isMaritime ? "vessels" : "aircrafts"}:
              </span>
              {!!vehiclesError && (
                <div className="text-red-500">Error fetching vehicles...</div>
              )}
              {vehiclesPending ? <Loader size="sm" inline /> : numberOfVehicles}
            </div>

            {/* Reporting Calendar / Trial End */}
            {existsDynamicReplacements(
              `customers__${customerCode}__trial_end_date`,
            ) && (
              <>
                <div className="pt-4 flex items-center text-primary-700 font-bold">
                  Reporting Calendar
                </div>
                <div className="flex items-center">
                  <span className="pr-2">
                    {AppUtils.capitalizeFirstLetter(typeOfContract)} end date:
                  </span>
                  {getDynamicReplacement(
                    `customers__${customerCode}__trial_end_date`,
                  )}
                </div>
              </>
            )}
          </>
        )}

        <div className="pt-4 flex items-center text-primary-700 font-bold">
          Signol Contacts
        </div>
        <div className="flex items-center">
          {getDynamicReplacement(`customers__${customerCode}__contact_title`)}
        </div>
        <div className="pt-2 pb-6 flex items-center">
          {existsDynamicReplacements(
            `customers__${customerCode}__contact_pic`,
          ) && (
            <ImageWithRetry
              src={getDynamicReplacement(
                `customers__${customerCode}__contact_pic`,
              )}
              alt="profile picture"
              height="42"
            />
          )}
          <div className="px-6">
            <div className="text-primary-700 font-bold">
              {getDynamicReplacement(
                `customers__${customerCode}__contact_name`,
              )}
            </div>
            <div className="text-gray-500">
              {getDynamicReplacement(
                `customers__${customerCode}__contact_email`,
              )}
            </div>
          </div>
        </div>
      </div>
    </CardContainer>
  );
};

export default ProfileCard;
